<template>
  <div class="container-fluid mt-5">
    <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
      <template slot="header">
        <div class="row" v-if="!!order.id">
          <div class="col-12">
            <h3 class="mb-0 view-header-buttons">
              {{ order.seq_number ?? order.code }}
              | {{ order.destinationEstablishment && order.destinationEstablishment.company_name }}
              &larr;
              {{ order.sourceEstablishment && order.sourceEstablishment.company_name }}
              <base-button class="mr-2" type="primary" size="sm" @click="goBack">
                {{ $t("COMMON.RETURN_TO_LIST") }}
              </base-button>
              <br /><br />

              <base-button
                v-if="
                  $currentUserCan($permissions.PERM_EDIT_ESTABLISHMENT_INTERNAL_ORDERS) &&
                  order.status === 'DRAFT'
                "
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :disabled="loading"
                @click="goToEdit"
              >
                <i class="fas fa-edit"></i>
                {{ $t("COMMON.EDIT") }}
              </base-button>

              <base-button
                v-if="order.status === 'DRAFT' && order.items && order.items.length > 0"
                class="mr-2"
                type="primary"
                icon
                size="sm"
                :loading="loading"
                @click="validate"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.SUBMIT") }}
              </base-button>

              <base-button
                v-if="
                  order.status === 'DRAFT' &&
                  $currentUserCan($permissions.PERM_DELETE_ESTABLISHMENT_INTERNAL_ORDERS)
                "
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="deleteOrder"
              >
                <i class="fas fa-trash"></i>
                {{ $t("COMMON.DELETE") }}
              </base-button>

              <base-button
                v-if="canDecide"
                class="mr-2"
                type="success"
                icon
                size="sm"
                :loading="loading"
                @click="accept"
              >
                <i class="ni ni-check-bold"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.CONFIRM") }}
              </base-button>

              <base-button
                v-if="canDecide"
                class="mr-2"
                type="warning"
                icon
                size="sm"
                :loading="loading"
                @click="openRejectModal"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.REJECT") }}
              </base-button>

              <base-button
                v-if="canRevertToDraft"
                class="mr-2"
                type="secondary"
                icon
                size="sm"
                :loading="loading"
                @click="revertToDraft"
              >
                <i class="fas fa-undo"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.REVERT_TO_DRAFT") }}
              </base-button>

              <base-button
                v-if="order.status === 'SUBMITTED'"
                class="mr-2"
                type="danger"
                icon
                size="sm"
                :loading="loading"
                @click="cancel"
              >
                <i class="ni ni-fat-remove"></i>
                {{ $t("COMMON.CANCEL") }}
              </base-button>

              <base-button
                v-if="order.status !== 'DRAFT'"
                class="mr-2"
                type="info"
                icon
                size="sm"
                @click="printWeek"
              >
                <i class="fas fa-print"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.PRINT_WEEK") }}
              </base-button>

              <base-button
                v-if="canSendMail"
                type="success"
                icon
                size="sm"
                @click="toggleMailModal"
              >
                <i class="fas fa-envelope"></i>
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.SEND_BY_MAIL") }}
              </base-button>
            </h3>
            <!-- The author cannot confirm nor reject their own week: say so rather
                 than leaving an empty toolbar. -->
            <div class="alert alert-secondary mb-0" v-if="isAuthor && order.status === 'SUBMITTED'">
              <i class="fas fa-info-circle mr-1"></i>
              {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.AUTHOR_CANNOT_DECIDE") }}
            </div>
          </div>
        </div>
      </template>

      <div
        class="col-12 justify-content-center justify-content-sm-between flex-wrap tabs-wrapper"
      >
        <tabs
          fill
          class="flex-column flex-md-row"
          tabNavWrapperClasses="nav-wrapper"
          tabNavClasses="nav nav-pills nav-fill"
          value="details"
        >
          <card shadow>
            <tab-pane title="global" id="1" :active="true">
              <span slot="title">
                <i class="ni ni-cloud-upload-96" />
                {{ $t("COMMON.GLOBAL") }}
              </span>
              <establishment-internal-order-view-global :order="order" />
            </tab-pane>

            <tab-pane title="details" id="2" :active="false">
              <span slot="title">
                <i class="ni ni-bullet-list-67" />
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.ITEMS_GRID") }}
              </span>
              <establishment-internal-order-items-grid
                :order="order"
                :saving="savingItems"
                @onSubmitOrderItem="handleItemSubmit"
                @onDeleteOrderItem="handleItemDelete"
                @onBulkOrderItems="handleBulkItems"
              />
              <div class="row mb-5" v-if="order.pricing">
                <div class="col-6"></div>
                <div class="col-6">
                  <dl class="row mb-0">
                    <dt class="col-sm-6">{{ $t("COMMON.SUBTOTAL") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(order.pricing.subtotal || 0) }}
                    </dd>
                  </dl>
                  <dl class="row mb-0" v-if="order.pricing.discounts?.amount">
                    <dt class="col-sm-6">
                      {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.DISCOUNT") }}
                    </dt>
                    <dd class="col-sm-6 text-right px-0 text-danger">
                      {{ $formatCurrency((order.pricing.discounts.amount || 0) * -1) }}
                    </dd>
                  </dl>
                  <dl class="row mb-0">
                    <dt class="col-sm-6">{{ $t("COMMON.TOTAL") }}</dt>
                    <dd class="col-sm-6 text-right px-0">
                      {{ $formatCurrency(order.pricing.total || 0) }}
                    </dd>
                  </dl>
                </div>
              </div>
            </tab-pane>

            <tab-pane title="tracking" id="3" :active="false" v-if="order.id">
              <span slot="title">
                <i class="ni ni-chart-bar-32" />
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.TRACKING") }}
              </span>
              <establishment-internal-order-view-tracking
                :order="order"
                @onInvoiceGenerated="getOrder"
              />
            </tab-pane>

            <tab-pane title="invoices" id="4" :active="false" v-if="order.id">
              <span slot="title">
                <i class="fas fa-file-invoice-dollar" />
                {{ $t("ESTABLISHMENT_INTERNAL_ORDERS.INTERNAL_INVOICES") }}
              </span>
              <establishment-internal-order-view-invoices :order="order" />
            </tab-pane>
          </card>
        </tabs>
      </div>
    </card>

    <modal
      v-if="showMailModal"
      :show="showMailModal"
      modal-classes="modal-secondary"
      size="xl"
    >
      <establishment-internal-order-mail-modal-content
        :order="order"
        :toggleMailModal="toggleMailModal"
      />
    </modal>

    <internal-document-reason-modal
      :show-modal="showRejectModal"
      :title="$t('INTERNAL_DOCUMENTS.REJECT_ORDER_TITLE')"
      :help="$t('INTERNAL_DOCUMENTS.REJECT_ORDER_HELP')"
      :reasons="rejectionReasons"
      reasons-key="REJECTION_REASONS"
      :confirm-label="$t('ESTABLISHMENT_INTERNAL_ORDERS.REJECT')"
      :loading="loading"
      @confirm="confirmReject"
      @close="showRejectModal = false"
    />
  </div>
</template>

<script>
import cloneDeep from "lodash/cloneDeep";
import swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.css";
import { Tabs, TabPane } from "@/components";
import requestErrorMixin from "@/mixins/request-error-mixin";
import { openPdfBlob } from "@/helpers/downloadPdf";
import defaultOrder from "./defaultEstablishmentInternalOrder";
import EstablishmentInternalOrderItemsGrid from "./partials/EstablishmentInternalOrderItemsGrid.vue";
import EstablishmentInternalOrderViewGlobal from "./partials/EstablishmentInternalOrderViewGlobal.vue";
import EstablishmentInternalOrderViewTracking from "./partials/EstablishmentInternalOrderViewTracking.vue";
import EstablishmentInternalOrderViewInvoices from "./partials/EstablishmentInternalOrderViewInvoices.vue";
import InternalDocumentReasonModal from "@/components/InternalDocumentReasonModal.vue";
import EstablishmentInternalOrderMailModalContent from "./partials/EstablishmentInternalOrderMailModalContent.vue";

export default {
  name: "ViewEstablishmentInternalOrderPage",
  components: {
    EstablishmentInternalOrderItemsGrid,
    EstablishmentInternalOrderViewGlobal,
    EstablishmentInternalOrderViewTracking,
    EstablishmentInternalOrderViewInvoices,
    EstablishmentInternalOrderMailModalContent,
    InternalDocumentReasonModal,
    Tabs,
    TabPane,
  },
  mixins: [requestErrorMixin],
  data() {
    return {
      order: cloneDeep(defaultOrder),
      showRejectModal: false,
      rejectionReasons: [
        "CAPACITY",
        "PRODUCTS_UNAVAILABLE",
        "WRONG_CONTENT",
        "OTHER",
      ],
      loading: false,
      savingItems: false,
      showMailModal: false,
    };
  },
  created() {
    this.getOrder();
    if (!this.$store.getters["profile/me"]) {
      this.$store.dispatch("profile/me");
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["profile/me"];
    },
    /**
     * The order can be e-mailed to the production establishment as soon as it left
     * DRAFT and is still live (a rejected or canceled week has nothing to send).
     */
    /**
     * Whoever wrote the week — created it or submitted it — cannot be the one
     * confirming or rejecting it. Mirrors EstablishmentInternalOrder::wasAuthoredBy.
     */
    isAuthor() {
      const me = this.currentUser;
      if (!me) return false;

      const authorIds = [this.order.createdBy?.id, this.order.submittedBy?.id].filter(
        Boolean
      );

      return authorIds.some((id) => String(id) === String(me.id));
    },
    canDecide() {
      return this.order.status === "SUBMITTED" && !this.isAuthor;
    },
    /**
     * Une commande rejetée se corrige et se resoumet plutôt que de tout
     * ressaisir ; il faut pouvoir la modifier ensuite.
     */
    canRevertToDraft() {
      if (this.order.status === "SUBMITTED") {
        return true;
      }
      return (
        this.order.status === "REJECTED" &&
        this.$currentUserCan(this.$permissions.PERM_EDIT_ESTABLISHMENT_INTERNAL_ORDERS)
      );
    },
    canSendMail() {
      return (
        !!this.order.id &&
        !["DRAFT", "REJECTED", "CANCELED"].includes(this.order.status) &&
        this.$currentUserCan(this.$permissions.PERM_VIEW_ESTABLISHMENT_INTERNAL_ORDERS)
      );
    },
  },
  methods: {
    async getOrder() {
      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalOrders/get", {
          id: this.$route.params.id,
        });
        this.order = this.$store.getters["establishmentInternalOrders/order"];
      } catch (error) {
        // Without this catch the page silently kept the default (empty draft)
        // order and the grid drew a misleading "production has no warehouse"
        // warning — the real cause (404, scope, network) was never surfaced.
        await this.showRequestError(error);
        this.$router.push({ name: "List EstablishmentInternalOrders" });
      } finally {
        this.loading = false;
      }
    },
    async validate() {
      if (!this.order.items || !this.order.items.length) {
        this.$notify({
          type: "danger",
          message: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.ADD_ITEMS_FIRST"),
        });
        return;
      }

      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.SUBMIT_QUESTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (!confirmation.value) return;

      const submitted = await this.runAction(
        "validate",
        "ESTABLISHMENT_INTERNAL_ORDER_SUBMITTED"
      );
      if (!submitted) return;

      // Same flow as a sales order submission: offer to e-mail it right away, then
      // let the user set the recipients, subject and body in the modal.
      const send = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.SEND_NOW_QUESTION"),
        text: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.SEND_NOW_HINT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (send.value) {
        this.toggleMailModal();
      }
    },
    async accept() {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.CONFIRM_QUESTION"),
        text: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.CONFIRM_FREEZE_HINT"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (!confirmation.value) return;

      await this.runAction("accept", "ESTABLISHMENT_INTERNAL_ORDER_CONFIRMED");
    },
    openRejectModal() {
      this.showRejectModal = true;
    },
    /**
     * Le refus remplace la simple confirmation oui/non : le satellite est averti
     * par courriel et doit savoir pourquoi sa semaine ne sera pas produite.
     */
    async confirmReject({ reasonCode, reason }) {
      const done = await this.runAction(
        "reject",
        "ESTABLISHMENT_INTERNAL_ORDER_REJECTED",
        { reasonCode, reason }
      );

      if (done) {
        this.showRejectModal = false;
      }
    },
    async cancel() {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.CANCEL_QUESTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (!confirmation.value) return;

      await this.runAction("cancel", "ESTABLISHMENT_INTERNAL_ORDER_CANCELED");
    },
    async revertToDraft() {
      const confirmation = await swal.fire({
        title: this.$t("ESTABLISHMENT_INTERNAL_ORDERS.REVERT_TO_DRAFT_QUESTION"),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (!confirmation.value) return;

      await this.runAction("revertToDraft", "ESTABLISHMENT_INTERNAL_ORDER_REVERTED");
    },
    async runAction(action, successKey, payload = {}) {
      this.loading = true;
      try {
        await this.$store.dispatch(`establishmentInternalOrders/${action}`, {
          id: this.order.id,
          ...payload,
        });
        await this.getOrder();
        this.$notify({
          type: "success",
          message: this.$t(`ESTABLISHMENT_INTERNAL_ORDERS.${successKey}`),
        });
        return true;
      } catch (error) {
        await this.showRequestError(error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    /**
     * Closing the modal refreshes the order so the "sent at / sent by" trace shows
     * up without a manual reload.
     */
    toggleMailModal() {
      this.showMailModal = !this.showMailModal;

      if (!this.showMailModal && this.order.id) {
        this.getOrder();
      }
    },
    async handleItemSubmit(itemData) {
      this.savingItems = true;
      try {
        await this.persistItem(itemData);
        await this.getOrder();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.savingItems = false;
      }
    },
    /**
     * Bulk helpers (copy monday, reuse previous week) go through the same
     * per-cell persistence, then refresh once.
     */
    async handleBulkItems(payloads) {
      this.savingItems = true;
      try {
        for (const { product, day, quantity } of payloads) {
          if (!quantity || quantity <= 0) continue;

          const existing = (this.order.items || []).find(
            (item) =>
              (item.storable?.id || item.storable_id) == product.id &&
              item.delivery_date === day
          );

          const itemData = existing
            ? { ...cloneDeep(existing), quantity }
            : {
                type: "establishment-internal-order-items",
                relationshipNames: ["unitOfMeasureUnit", "storable", "internalOrder"],
                storable: { type: "products", id: product.id },
                unitOfMeasureUnit:
                  product.unitOfMeasure && product.unitOfMeasure.referenceUnit
                    ? product.unitOfMeasure.referenceUnit
                    : { type: "unit-of-measure-units", id: null },
                delivery_date: day,
                quantity,
              };

          await this.persistItem(itemData);
        }
        await this.getOrder();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.savingItems = false;
      }
    },
    persistItem(itemData) {
      const payload = {
        ...itemData,
        internalOrder: {
          type: "establishment-internal-orders",
          id: this.order.id,
        },
      };

      return itemData.id
        ? this.$store.dispatch("establishmentInternalOrderItems/update", payload)
        : this.$store.dispatch("establishmentInternalOrderItems/add", payload);
    },
    async handleItemDelete(id) {
      this.savingItems = true;
      try {
        await this.$store.dispatch("establishmentInternalOrderItems/destroy", id);
        await this.getOrder();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.savingItems = false;
      }
    },
    async deleteOrder() {
      const confirmation = await swal.fire({
        title: this.$t(
          "ESTABLISHMENT_INTERNAL_ORDERS.DELETE_THIS_ESTABLISHMENT_INTERNAL_ORDER"
        ),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("COMMON.YES"),
        cancelButtonText: this.$t("COMMON.NO"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });

      if (!confirmation.value) return;

      this.loading = true;
      try {
        await this.$store.dispatch("establishmentInternalOrders/destroy", {
          id: this.order.id,
        });
        this.$notify({
          type: "success",
          message: this.$t(
            "ESTABLISHMENT_INTERNAL_ORDERS.ESTABLISHMENT_INTERNAL_ORDER_DELETED"
          ),
        });
        this.goBack();
      } catch (error) {
        await this.showRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    async printWeek() {
      swal.fire({
        title: this.$t("COMMON.LOADING"),
        onBeforeOpen: () => {
          swal.showLoading();
        },
      });
      try {
        const response = await this.$store.dispatch("establishmentInternalOrders/print", {
          id: this.order.id,
        });
        openPdfBlob(response, `${this.order.seq_number || this.order.code}.pdf`);
        swal.close();
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        swal.close();
      }
    },
    goToEdit() {
      this.$router.push({
        name: "Edit EstablishmentInternalOrder",
        params: { id: this.order.id },
      });
    },
    goBack() {
      this.$router.push({ name: "List EstablishmentInternalOrders" });
    },
  },
};
</script>
